<template lang="pug">
div
    v-text-field(
        v-model="timeInput"
        :label="inputLabel"
        type="text"
        disabled
        :rules="[v => isEmptyInput || validTime || 'Invalid time']"
        prepend-icon="mdi-clock-outline"
        :error-messages="errorMessageNotInWindow"
        clearable
    )
    v-container
        v-row
            v-col(:cols="hourCols").pl-0
                .h6.text-left.ma-1 Hour
                template(v-for="(hour, hIndex) in hours")
                    v-btn(
                        @click="handleHourInput(hour)"
                        :disabled="disableInput"
                        x-small
                        :key="'h-'+hIndex"
                        :color="getBtnColor('hour', hour)"
                    ).ma-1 {{ hour }}
            v-col(:cols="minuteCols")
                .h6.text-left.ma-1 Minutes
                template(v-for="(minuteVal, mIndex) in minutesArray")
                    v-btn(
                        @click="handleMinuteInput(minuteVal)"
                        :disabled="disableInput"
                        x-small
                        :key="'m-'+mIndex"
                        :color="getBtnColor('minute', minuteVal)"
                    ).ma-1 {{ minuteVal }}
            v-col(cols="2").pr-0
                .h6.text-center.ma-1.invisible .
                v-btn(
                    @click="handleAMPMInput('AM')"
                    :disabled="disableInput"
                    x-small
                    :color="getBtnColor('am/pm','AM')"
                ).ma-1 AM
                v-btn(
                    @click="handleAMPMInput('PM')"
                    :disabled="disableInput"
                    x-small
                    :color="getBtnColor('am/pm','PM')"
                ).ma-1 PM




</template>

<script lang="ts">
    import Vue from 'vue';

    // Helpers
    import { timeMilitaryToFloat } from "@/helpers/general";

    export default Vue.extend({
        data() {
            return {
                fiveMinutes: [
                    "00","05","10","15","20","25","30","35","40","45","50","55",
                ],
                fifteenMinutes: [
                    "00","15","30","45",
                ],
                hours: [
                    "7","8","9","10","11","12","1","2","3","4","5","6"
                ],
                inputLabel: null,
                timeInput: '07:00 AM',
                timeValue: null,
            }
        },
        methods: {
            getBtnColor(valueType, value) {

                // add leading zero to value if only one digit
                if (value.length === 1) {
                    value = '0' + value;
                }

                // If clicked, return primary, otherwise return primary
                switch (valueType) {
                    case 'hour':
                        if (this.timeInput.substring(0, 2) === value) {
                            return 'primary';
                        }
                        break;
                    case 'minute':
                        if (this.timeInput.substring(3, 5) === value) {
                            return 'primary';
                        }
                        break;
                    case 'am/pm':
                        if (this.timeInput.substring(6, 8).toUpperCase() === value.toUpperCase()) {
                            return 'primary';
                        }
                        break;
                    default:
                        return 'var(--off-white)';
                }
                return 'var(--off-white)';
            },
            handleChange() {
                this.updateTimeValue();
                if (this.validTime && this.isTimeWithinWindow) {
                    this.onChange(this.timeValue.substring(0, 5));
                    return;
                }
                this.onChange(null);
                return;
            },
            handleHourInput(hour) {
                if (hour.length === 1) {
                    hour = '0' + hour;
                }
                this.timeInput = hour + this.timeInput.substring(2);
                this.handleChange();
            },
            handleMinuteInput(minute) {
                this.timeInput = this.timeInput.substring(0, 3) + minute + this.timeInput.substring(5);
                this.handleChange();
            },
            handleAMPMInput(ampm) {
                this.timeInput = this.timeInput.substring(0, 6) + ampm;
                this.handleChange();
            },
            setLabel() {
                if (this.label?.length > 0) {
                    this.inputLabel = this.label;
                    return;
                }

                this.inputLabel = "Time";
                return;
            },
            updateTimeValue() {
                this.timeInput = this.timeInput.trimStart();

                // if invalid input set timeValue to null
                if (this.timeInput?.length !== 8) {
                    this.timeValue = null;
                }

                // Get the hour part
                const hourPart = this.timeInput.substring(0, 2);
                // Get minutes part
                const minutesPart = this.timeInput.substring(3, 5);
                // Get AM/PM part
                const amPmPart = this.timeInput.substring(6, 8).toUpperCase();

                // Convert to 24-hour format
                let hour24 = parseInt(hourPart, 10);

                if (hourPart === '12') {
                    hour24 = (amPmPart === 'AM') ? 0 : 12;
                } else if (amPmPart === 'PM') {
                    hour24 += 12;
                }

                // Format the 24-hour time
                const hour24Str = hour24 < 10 ? `0${hour24}` : `${hour24}`;
                this.timeValue = `${hour24Str}:${minutesPart}:00`;

            },
        },
        props: {
            accountMinutes: String,
            appointmentSchedulingHour: Object,
            dashboardDefaultDate: String,
            dateValue: String,
            disableInput: Boolean,
            label: String,
            onChange: Function,
            value: String,
        },
        computed: {
            appointmentSchedulingWindow() {

                // Ensure valid
                if (!this.validAppointmentSchedulingHour) {
                    return null;
                }

                // Check if closed
                if (this.isWindowDayClosed) {
                    return 'CLOSED on ' + (this.appointmentSchedulingHour.day.charAt(0).toUpperCase() + this.appointmentSchedulingHour.day.slice(1)) + 's';
                }

                return 'Valid scheduling hours for ' + (this.appointmentSchedulingHour.day.charAt(0).toUpperCase() + this.appointmentSchedulingHour.day.slice(1)) + ': ' + this.appointmentSchedulingHour.start_time_hour_value + ':' + this.appointmentSchedulingHour.start_time_minute_value + ' ' + this.appointmentSchedulingHour.start_time_am_pm_value + ' - ' + this.appointmentSchedulingHour.end_time_hour_value + ':' + this.appointmentSchedulingHour.end_time_minute_value + ' ' + this.appointmentSchedulingHour.end_time_am_pm_value;
            },
            errorMessageNotInWindow() {
                return (this.validTime || this.isWindowDayClosed) && !this.isTimeWithinWindow ? this.appointmentSchedulingWindow : null;
            },
            hourCols() {
                if (this.accountMinutes === 'five_minutes' || this.dashboardDefaultDate) {
                    return 5;
                } else {
                    return 6;
                }
            },
            minuteCols() {
                if (this.accountMinutes === 'five_minutes' || this.dashboardDefaultDate) {
                    return 5;
                } else {
                    return 4;
                }
            },
            isEmptyInput() {
                return !(this.timeInput?.length > 0);
            },
            isTimeWithinWindow() {

                // Verify we have a valid window
                if (!this.validAppointmentSchedulingHour) {
                    return true;
                }

                // Check if we are closed on current day
                if (this.isWindowDayClosed) {
                    return false;
                }

                // Ensure valid time
                if (!this.validTime) {
                    return false;
                }

                // Convert to military time as a number
                const timeValue = timeMilitaryToFloat(this.timeValue.substring(0, 5));

                // Ensure it is within the scheduling window
                if (timeValue < this.appointmentSchedulingHour.start_time_hour || timeValue >= this.appointmentSchedulingHour.end_time_hour) {
                    return false;
                }

                return true;
            },
            isWindowDayClosed() {

                // Verify we have a valid window
                if (!this.validAppointmentSchedulingHour) {
                    return false;
                }

                return this.appointmentSchedulingHour.is_closed === true;
            },
            minutesArray() {
                if (this.accountMinutes == 'five_minutes') {
                    return this.fiveMinutes;
                } else if (this.accountMinutes == 'fifteen_minutes') {
                    return this.fifteenMinutes;
                } else {
                    return this.anytime;
                }
            },
            validAppointmentSchedulingHour() {
                return this.appointmentSchedulingHour?.appointment_scheduling_hour_id > 0;
            },
            validTime() {
                return this.timeValue?.length === 8;
            },
        },
        components: {},
        created() {
            this.setLabel();
            // In case value is already preset, check
            if (this.value?.length > 0) {

                // convert value to non-military time in format HH:MM AM/PM with a leading zero if needed for HH and MM
                const hour = parseInt(this.value.substring(0, 2), 10);
                const minutes = this.value.substring(3, 5);
                const amPm = hour >= 12 ? 'PM' : 'AM';
                const hour12 = hour % 12 || 12; // Convert to 12-hour format, ensuring 12 is displayed as 12
                this.timeInput = `${hour12.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${amPm}`;
                this.handleChange()

            }
        },
        watch: {
            dateValue: {
                handler (next, old) {
                    // if date value changes from outside the timepicker, we need to recheck if the time is within the window
                    if (next !== old) {
                        this.handleChange();
                    }
                }
            },
        },
    });
</script>

<style lang="scss">
</style>
